import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { comics, getComicById, defaultReadingPanels } from "@/mocks/comics";
import { categories } from "@/mocks/categories";
import ComicCard from "@/components/base/ComicCard";

export default function ComicPage() {
  const { id = "" } = useParams();
  const comic = getComicById(id);
  const [reading, setReading] = useState<number | null>(null);
  const [chapter, setChapter] = useState(0);

  useEffect(() => {
    setReading(null);
    setChapter(0);
  }, [id]);

  const related = useMemo(() => {
    if (!comic) return [];
    return comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <div className="w-full max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading text-2xl text-foreground-900 mb-4">未找到该漫画</h1>
        <p className="text-sm text-foreground-600 mb-6">你访问的漫画不存在或已被下架。</p>
        <Link to="/category/all" className="inline-flex rounded-md bg-primary-500 text-background-50 px-5 py-2.5 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          返回全部漫画
        </Link>
      </div>
    );
  }

  const catName = categories.find((c) => c.slug === comic.category)?.name ?? comic.category;
  const currentChapter = comic.chapters[chapter];
  const panels = comic.panels && comic.panels.length ? comic.panels : defaultReadingPanels;

  if (reading !== null) {
    return (
      <div className="min-h-screen bg-foreground-950">
        <div className="max-w-3xl mx-auto px-4">
          <div className="sticky top-16 z-40 bg-foreground-950/95 backdrop-blur py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setReading(null)}
              className="inline-flex items-center gap-1.5 text-background-100 text-sm hover:text-accent-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              返回详情
            </button>
            <h1 className="text-sm md:text-base text-background-100 truncate font-medium">{comic.title} · {currentChapter.title}</h1>
            <span className="text-xs text-background-400 whitespace-nowrap">{chapter + 1}/{comic.chapters.length}</span>
          </div>

          <div className="py-6 flex flex-col items-center gap-4">
            {panels.map((p, i) => (
              <img
                key={`${chapter}-${i}`}
                src={p}
                alt={`${comic.title} ${currentChapter.title} 阅读页面 ${i + 1}`}
                title={`${comic.title} 漫画阅读`}
                className="w-full max-w-[540px] rounded-lg object-contain bg-foreground-900"
              />
            ))}
          </div>

          <div className="pb-10 flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={chapter === 0}
              onClick={() => setChapter((c) => Math.max(0, c - 1))}
              className="rounded-md bg-background-200 text-foreground-900 px-5 py-2.5 text-sm whitespace-nowrap hover:bg-background-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              上一话
            </button>
            <span className="text-sm text-background-300 whitespace-nowrap">{currentChapter.title}</span>
            <button
              type="button"
              disabled={chapter >= comic.chapters.length - 1}
              onClick={() => setChapter((c) => Math.min(comic.chapters.length - 1, c + 1))}
              className="rounded-md bg-primary-500 text-background-50 px-5 py-2.5 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              下一话
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 md:px-6 py-6 md:py-8">
      <div className="max-w-7xl mx-auto">
        <nav className="text-sm text-foreground-500 mb-6 flex flex-wrap items-center gap-1.5" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          <Link to="/category/all" className="hover:text-primary-600 transition-colors">漫画分类</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors">{catName}漫画</Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
          <span className="text-foreground-700 truncate">{comic.title}</span>
        </nav>

        <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
          <div className="w-40 sm:w-52 md:w-56 shrink-0 mx-auto md:mx-0">
            <img
              src={comic.cover}
              alt={`${comic.title} 漫画封面`}
              title={`${comic.title} 漫画全集免费阅读`}
              className="w-full aspect-[3/4] rounded-lg object-cover object-top border border-background-200/70"
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{comic.title}</h1>
            <p className="text-sm text-foreground-600 flex flex-wrap gap-x-5 gap-y-2">
              <span>作者：{comic.author}</span>
              <span>状态：{comic.status}</span>
              <span>共{comic.totalChapters}话</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />{comic.views} 人气</span>
            </p>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-accent-600 text-sm">
                <i className="ri-star-fill w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                {comic.rating.toFixed(1)} 分
              </span>
              <span className="text-xs text-foreground-400">({comic.favorite} 人收藏)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {comic.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 text-secondary-900 text-xs px-3 py-1">{t}</span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <button
                type="button"
                onClick={() => setReading(comic.chapters.length - 1)}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium px-6 py-3 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line w-5 h-5 flex items-center justify-center" aria-hidden="true" />
                开始阅读
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm font-medium px-5 py-3 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-heart-3-line w-5 h-5 flex items-center justify-center" aria-hidden="true" />
                收藏
              </button>
              <span className="text-xs text-foreground-500">最近更新：{comic.updateDate} · {comic.lastChapter}</span>
            </div>
            <div>
              <h2 className="font-heading text-base text-foreground-900 mb-2">漫画简介</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
            </div>
          </div>
        </div>

        <div className="mb-10">
          <h2 className="font-heading text-xl text-foreground-900 mb-4">章节列表</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {comic.chapters.map((ch, i) => (
              <li key={ch.id}>
                <button
                  type="button"
                  onClick={() => setReading(i)}
                  className="w-full flex items-center justify-between gap-3 rounded-md bg-background-100 border border-background-200/70 px-4 py-3 text-left text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <span className="truncate">{ch.title}</span>
                  <i className="ri-play-circle-line w-5 h-5 flex items-center justify-center text-foreground-400 shrink-0" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <section aria-labelledby="related-title">
          <h2 id="related-title" className="font-heading text-xl text-foreground-900 mb-4">同类推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}